import React from 'react';

import { Box } from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers-pro';

import { Dayjs, parseDayjs } from '~/services/TimeService';

type Props = {
  value: string;
  onCommit: (isoValue: string) => void;
};

/**
 * Persists `condition_value` as ISO 8601 (UTC) via `Dayjs#toISOString()` for backend compatibility.
 */
export default function ConditionValueDatetimeField({ value, onCommit }: Props) {
  const parsed = parseDayjs(value);
  return (
    <Box sx={{ px: 1, py: 0.5, width: '100%', minWidth: 260 }} onKeyDown={(e) => e.stopPropagation()}>
      <DateTimePicker
        value={parsed.isValid() ? parsed : null}
        onChange={(newValue: Dayjs | null) => {
          onCommit(newValue?.isValid() ? newValue.toISOString() : '');
        }}
        slotProps={{
          textField: { size: 'small', fullWidth: true, required: true },
        }}
        timeSteps={{ minutes: 1 }}
      />
    </Box>
  );
}

export function formatIsoDatetimeForConditionDisplay(iso: string): string {
  const d = parseDayjs(iso);
  return d.isValid() ? d.format('YYYY-MM-DD HH:mm:ss Z') : iso;
}

/**
 * For datetime `condition_value` with apply type BEFORE-DATE / AFTER-DATE: compares **now** to the stored instant
 * (same idea as `DateFormatterDetailed` `highlightIfPast` — green when the rule is in a satisfied window, red when not).
 *
 * - **BEFORE-DATE**: satisfied while `now <= condition` (still on or before the cutoff).
 * - **AFTER-DATE**: satisfied while `now >= condition` (on or after the start).
 */
export function getConditionDatetimeApplyHighlightColor(
  conditionApplyType: string | undefined,
  conditionValueIso: string,
): 'success' | 'error' | undefined {
  if (conditionApplyType !== 'BEFORE-DATE' && conditionApplyType !== 'AFTER-DATE') {
    return undefined;
  }
  const cutoff = parseDayjs(conditionValueIso);
  if (!cutoff.isValid()) {
    return undefined;
  }
  const now = parseDayjs(new Date());
  if (conditionApplyType === 'BEFORE-DATE') {
    return !now.isAfter(cutoff) ? 'success' : 'error';
  }
  return !now.isBefore(cutoff) ? 'success' : 'error';
}
